import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, Mail, Phone, MapPin, Briefcase, Calendar, X, Download, RefreshCw } from "lucide-react";
import { adminClient } from "@/lib/apiClient";
import {
  CAREER_CATEGORIES,
  CATEGORY_LABEL,
  CAREER_APP_STATUSES,
  STATUS_LABEL,
} from "@/data/careerTemplates";

function StatusPill({ status }) {
  const meta = CAREER_APP_STATUSES.find((s) => s.slug === status) || CAREER_APP_STATUSES[0];
  const toneMap = {
    slate: "bg-slate-200 text-slate-700",
    amber: "bg-amber-100 text-amber-700",
    sky: "bg-sky-100 text-sky-700",
    violet: "bg-violet-100 text-violet-700",
    red: "bg-red-100 text-red-700",
    emerald: "bg-emerald-100 text-emerald-700",
    gray: "bg-gray-200 text-gray-600",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${toneMap[meta.tone] || toneMap.gray}`}>
      {meta.label}
    </span>
  );
}

function ApplicationDetailDrawer({ app, onClose, onStatusChange }) {
  if (!app) return null;
  return (
    <div
      className="fixed inset-0 z-[150] bg-br-black/70 backdrop-blur-sm flex items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="career-app-drawer"
    >
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-br-paper">
          <div>
            <div className="br-eyebrow text-br-bronze">Career Application</div>
            <h3 className="font-display text-xl tracking-widest uppercase mt-1">{app.full_name}</h3>
            <div className="text-xs text-gray-600 mt-0.5">{app.position_title || "General application"} — {CATEGORY_LABEL[app.category] || "—"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-br-black" aria-label="Close" data-testid="career-app-drawer-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-gray-700"><Phone size={14} className="text-br-bronze" /> {app.phone}</div>
            <div className="flex items-center gap-2 text-gray-700"><Mail size={14} className="text-br-bronze" /> {app.email}</div>
            {app.city && <div className="flex items-center gap-2 text-gray-700"><MapPin size={14} className="text-br-bronze" /> {app.city}</div>}
            <div className="flex items-center gap-2 text-gray-700"><Calendar size={14} className="text-br-bronze" /> {new Date(app.created_at).toLocaleString()}</div>
          </div>

          {/* Status workflow */}
          <div className="bg-br-paper border border-gray-200 rounded p-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">Application Status</div>
            <div className="flex flex-wrap gap-1.5" data-testid="career-app-status-row">
              {CAREER_APP_STATUSES.map((s) => {
                const isActive = app.status === s.slug;
                return (
                  <button
                    key={s.slug}
                    type="button"
                    onClick={() => onStatusChange(app, s.slug)}
                    className={`px-3 py-1 rounded text-[10px] uppercase tracking-widest font-semibold border transition ${isActive ? "bg-br-bronze border-br-bronze text-white" : "bg-white border-gray-200 text-gray-700 hover:border-br-bronze"}`}
                    data-testid={`career-app-status-${s.slug}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Qualifiers */}
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              ["Construction experience", app.has_construction_experience],
              ["Sales experience", app.has_sales_experience],
              ["Reliable transport", app.has_reliable_transportation],
              ["Own tools", app.has_own_tools],
              ["Comfortable in the field", app.comfortable_with_field_work],
              ["Comfortable on commission", app.comfortable_with_commission],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm border border-gray-100 rounded px-3 py-2 bg-br-paper/40">
                <span className="text-gray-700">{k}</span>
                <span className="font-semibold text-gray-900">{v === true ? "Yes" : v === false ? "No" : "—"}</span>
              </div>
            ))}
          </div>

          {app.current_status && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1">Current work status</div>
              <p className="text-sm text-gray-800">{app.current_status}</p>
            </div>
          )}
          {app.relevant_experience && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1">Relevant experience</div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{app.relevant_experience}</p>
            </div>
          )}
          {app.desired_compensation && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1">Desired compensation</div>
              <p className="text-sm text-gray-800">{app.desired_compensation}</p>
            </div>
          )}
          {app.availability && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1">Availability</div>
              <p className="text-sm text-gray-800">{app.availability}</p>
            </div>
          )}
          {app.why_black_ridge && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1">Why Black Ridge</div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{app.why_black_ridge}</p>
            </div>
          )}
          {app.additional_notes && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-1">Notes</div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{app.additional_notes}</p>
            </div>
          )}

          {/* Files */}
          {Array.isArray(app.files) && app.files.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-2">Files</div>
              <ul className="space-y-2">
                {app.files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm bg-br-paper border border-gray-200 rounded px-3 py-2">
                    <Briefcase size={14} className="text-br-bronze" />
                    <span className="truncate flex-1">{f.filename || `file-${i + 1}`}</span>
                    <a href={f.data_url} download={f.filename || `file-${i + 1}`} className="text-br-bronze hover:text-br-bronze-2 text-[11px] uppercase tracking-widest font-bold" data-testid={`career-app-file-${i}`}>
                      <Download size={12} className="inline" /> Download
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminCareerApplications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: "", category: "", search: "" });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      const res = await adminClient.get("/admin/career-applications", { params });
      setApps(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load applications.");
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.category]);

  useEffect(() => { load(); }, [load]);

  const filteredApps = useMemo(() => {
    if (!filters.search) return apps;
    const q = filters.search.toLowerCase();
    return apps.filter((a) =>
      (a.full_name && a.full_name.toLowerCase().includes(q)) ||
      (a.email && a.email.toLowerCase().includes(q)) ||
      (a.position_title && a.position_title.toLowerCase().includes(q))
    );
  }, [apps, filters.search]);

  const openApp = async (app) => {
    // Fetch full record with file blobs for the drawer.
    try {
      const res = await adminClient.get(`/admin/career-applications/${app.id}`);
      setSelected(res.data);
    } catch (err) {
      setSelected(app);
    }
  };

  const updateStatus = async (app, status) => {
    try {
      await adminClient.patch(`/admin/career-applications/${app.id}`, { status });
      setSelected((s) => (s && s.id === app.id ? { ...s, status } : s));
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Could not update status.");
    }
  };

  return (
    <div data-testid="admin-career-applications">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="br-eyebrow">Recruiting</div>
          <h2 className="font-display text-2xl tracking-widest uppercase mt-1">Career Applications</h2>
          <p className="text-sm text-gray-600 mt-1">Review applicants, filter by position or status, and progress through the hiring workflow.</p>
        </div>
        <button type="button" onClick={load} className="br-btn-dark text-[11px]" data-testid="admin-careers-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>

      {/* Filters */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="admin-careers-filters">
        <div>
          <label className="label-br">Search</label>
          <input type="text" className="input-br" placeholder="Name, email, or position" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="admin-careers-filter-search" />
        </div>
        <div>
          <label className="label-br">Status</label>
          <select className="select-br" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="admin-careers-filter-status">
            <option value="">All</option>
            {CAREER_APP_STATUSES.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label-br">Category</label>
          <select className="select-br" value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} data-testid="admin-careers-filter-category">
            <option value="">All</option>
            {CAREER_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4" data-testid="admin-careers-apps-error">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500" data-testid="admin-careers-apps-loading">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading applications…
        </div>
      ) : filteredApps.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-8 text-center text-sm text-gray-500" data-testid="admin-careers-apps-empty">
          No applications match your filters.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-br-paper border-b border-gray-200">
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-gray-600">
                  <th className="px-4 py-3 font-bold">Applicant</th>
                  <th className="px-4 py-3 font-bold">Position</th>
                  <th className="px-4 py-3 font-bold">Location</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredApps.map((a) => (
                  <tr key={a.id} className="hover:bg-br-paper/30 cursor-pointer" onClick={() => openApp(a)} data-testid={`career-app-row-${a.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-gray-900">{a.full_name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{a.email} · {a.phone}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">
                      <div>{a.position_title || "General"}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{CATEGORY_LABEL[a.category] || "—"}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700">{a.city || "—"}</td>
                    <td className="px-4 py-3 align-top"><StatusPill status={a.status} /></td>
                    <td className="px-4 py-3 align-top text-[11px] text-gray-500">{new Date(a.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ApplicationDetailDrawer app={selected} onClose={() => setSelected(null)} onStatusChange={updateStatus} />
    </div>
  );
}
